import React, { useState, useEffect, useRef, useCallback } from 'react';
import useWebSocket from 'react-use-websocket';
import BaseChat from './BaseChat';
import { getDomainCfg, store } from '../../core/state';
import { translate } from '../../core/i18n';
import * as api from '../../features/chatrequests';
import { CHAT_VIEW_NAME, EDITOR_VIEW_NAME } from '../../core/constants';
import { runChatPostEvaluation } from '../../features/chatevaluate';
import { saveUserData } from '../../core/code/saver';
import { userHasRoleFromList } from '../../features/cloudstorage/user';
import { getViewData } from '../../core/views';

interface SupportChatWebSocketProps {
  hasProjectContext: boolean;
  resetMessages?: boolean;
  isAuthenticated: boolean;
  onReAuthenticate: () => Promise<boolean>;
}

interface RoomMessage {
  sender: string;
  message: string;
  role?: string;
  timestamp: Date;
}

interface ChatMessage {
  user: string;
  color: string;
  msg: string;
  messageType?: string;
  timestamp: Date;
}

interface WebSocketMessage {
  type: string;
  action?: string;
  roomId?: string;
  message?: string;
  sessionId?: string;
  status?: any;
  response?: any;
  messages?: RoomMessage[];
}

const SupportChatWebSocket: React.FC<SupportChatWebSocketProps> = ({
  hasProjectContext,
  resetMessages,
  isAuthenticated,
  onReAuthenticate
}) => {
  const [chatState, setChatState] = store.useState(CHAT_VIEW_NAME);
  const [editorState, setEditorState] = store.useState(EDITOR_VIEW_NAME);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [roomMessages, setRoomMessages] = useState<RoomMessage[]>([]);
  const [evaluatingChat, setEvaluatingChat] = useState(false);
  const [typingDots, setTypingDots] = useState(0);
  const [typingMessage, setTypingMessage] = useState(translate('chat-server-responding'));
  const [serverIsResponding, setServerIsResponding] = useState(false);
  const [workflowSessionId, setWorkflowSessionId] = useState<string | null>(null);
  const [currentRoomId, setCurrentRoomId] = useState<string | null>(null);
  const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
  const [wfStatus, setWfStatus] = useState({ status: 'idle', message: null, stepState: null, currentStep: null });
  const typingTimer = useRef<NodeJS.Timeout | null>(null);
  const responseStartTimeRef = useRef<number | null>(null);
  const rotatingMessages = ['denkt', 'tippt', 'prüft', 'formuliert', 'überlegt', 'analysiert'];
  const llmSwitchAvailable = chatState?.cfg?.llmSwitchAvailable === true;
  const modelOptions: { id: string; name: string; roles?: string[] }[] = Array.isArray(chatState?.cfg?.switchModels) ? chatState.cfg.switchModels : [];

  // Hilfsfunktion: Prüft ob ein Modell für den aktuellen Benutzer verfügbar ist
  const isModelAvailable = (model: { id: string; name: string; roles?: string[] }): boolean => {
    if (!model.roles || !Array.isArray(model.roles) || model.roles.length === 0) {
      return true; // Modell ohne Rollen-Einschränkung ist verfügbar
    }
    return userHasRoleFromList(model.roles);
  };

  // Finde das erste verfügbare Modell
  const getFirstAvailableModel = (): string | undefined => {
    const availableModel = modelOptions.find(opt => isModelAvailable(opt));
    return availableModel?.id;
  };

  // Initialisiere mit geladenem Modell oder erstem verfügbaren Modell
  const savedModelId = editorState.data?.lastUsedModelId;
  const savedModel = savedModelId ? modelOptions.find(opt => opt.id === savedModelId) : null;
  const initialModelId = (savedModel && isModelAvailable(savedModel)) ? savedModelId : getFirstAvailableModel();
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(initialModelId);

  useEffect(() => {
    // Lade gespeichertes Modell beim Initialisieren, wenn verfügbar
    if (savedModelId && savedModel && isModelAvailable(savedModel)) {
      setSelectedModelId(savedModelId);
    } else if (!selectedModelId) {
      const availableModelId = getFirstAvailableModel();
      if (availableModelId) {
        setSelectedModelId(availableModelId);
      }
    }
  }, [modelOptions, selectedModelId, editorState.data?.lastUsedModelId]);

  const [socketUrl, setSocketUrl] = useState<string | null>(null);
  const [streamedContent, setStreamedContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [reconnectCount, setReconnectCount] = useState(0);
  const MAX_RECONNECT_ATTEMPTS = 3;

  const [savePending, setSavePending] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingMessagesRef = useRef<RoomMessage[] | null>(null);

  useEffect(() => {
    if (editorState.data?.chatMessages && roomMessages.length === 0) {
      //console.log("SupportChatWebSocket: set initial messages", editorState.data?.chatMessages)
      setRoomMessages(editorState.data?.chatMessages);
    }
  }, [editorState.data?.chatMessages])

  useEffect(() => {
    if (isAuthenticated) {
      const authToken = localStorage.getItem('sessionToken');
      if (authToken) {
        const wsUrl = `${getDomainCfg().chatSettings.chatUrl.replace(/^http/, 'ws')}ws?token=${authToken}`;
        setSocketUrl(wsUrl);
        setReconnectCount(0);
        console.log('SupportChatWebSocket: authenticated');
      } else {
        console.error('SupportChatWebSocket: No authentication token available');
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!serverIsResponding) {
      const updateCredits = async () => {
        await fetchAndUpdateCredits();
      };
      updateCredits();
    }
  }, [serverIsResponding]);

  const fetchAndUpdateCredits = async () => {
    try {
      const creditData = await api.fetchUserCredits();
      const newChatState = {
        ...chatState,
        data: {
          ...chatState.data,
          credits: creditData
        }
      };
      setChatState(newChatState);
      console.log('SupportChatWebSocket: fetched creditData ', chatState, creditData)
    } catch (error) {
      console.error('Error fetching and updating credits:', error);
    }
  };

  const handleWebSocketClose = useCallback((event: CloseEvent) => {
    //console.log('SupportChatWebSocket: WS: WebSocket connection closed.', event);
    if (reconnectCount >= MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached. Please refresh the page or re-authenticate.');
      // You could trigger a re-authentication here if needed
      // For example, by setting a state that the parent component can react to
    }
    setHasSentFirstMessage(false);
  }, [reconnectCount]);

  const { sendMessage: sendWebSocketMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    onOpen: () => {
      setReconnectCount(0); // Reset count on successful connection
      //console.log('SupportChatWebSocket: WS: WebSocket connection opened.');
      //TODOif sessionsId or roomId is available, send or verify it via sendWebSocketMessage...?
      if (currentRoomId) {
        sendWebSocketMessage(JSON.stringify({
          type: 'join',
          action: 'join',
          roomId: currentRoomId
        }));
      }
    },
    onClose: handleWebSocketClose,
    onError: (error) => {
      console.error('SupportChatWebSocket: WebSocket error', error);
      handleError(error);
    },
    shouldReconnect: (closeEvent) => {
      if (reconnectCount < MAX_RECONNECT_ATTEMPTS) {
        setReconnectCount(prevCount => prevCount + 1);
        return true;
      }
      return false;
    },
    reconnectInterval: 3000,
    reconnectAttempts: MAX_RECONNECT_ATTEMPTS,
  });

  useEffect(() => {
    if (lastMessage !== null) {
      const data: WebSocketMessage = JSON.parse(lastMessage.data);
      handleWebSocketMessage(data);
    }
  }, [lastMessage]);

  const handleWebSocketMessage = (data: WebSocketMessage) => {
    if (data.status === 'error') {
      // Setze Fehlermeldung nur, wenn es ein Token-Fehler ist (wird später entfernt wenn Re-Auth erfolgreich)
      // Für andere Fehler wird die Meldung in handleErrorMessage gesetzt
      if (data.type === 'token-missing' || data.type === 'auth' || data.type === 'token-invalid') {
        setErrorMessage('Chatsitzung abgelaufen. Bitte hier Klicken um Chat neu zu laden - Dein Chatverlauf bleibt gespeichert (Meldung: ' + data.message + ')');
      }
      handleErrorMessage(data);
      setServerIsResponding(false);
      responseStartTimeRef.current = null;
      return;
    }
    switch (data.type) {
      case 'room':
        if (data.action === 'getMessages') {
          const oldMessages = roomMessages;
          if (data.messages.length > 0 && oldMessages.length !== data.messages.length) {
            saveMessagesState(data.messages);
          }

          setRoomMessages(data.messages || []);
          //console.log('SupportChatWebSocket: WS: getMessages', data.messages);
        } else if (data.status === 'STREAMING') {
          handleStreamingMessage(data);
        }
        break;
      case 'workflow':
        if (data.action === 'getStatus') {
          //console.log('SupportChatWebSocket: WS: getStatus', data.response);
          updateWorkflowStatus(data.response);
        }
        break;
    }
  };

  const handleError = (data: WebSocketMessage) => {
    if (data.type === 'room-access') {
      console.log('Room access error, attempting to rejoin');
      if (currentRoomId) {
        sendWebSocketMessage(JSON.stringify({
          type: 'join',
          action: 'join',
          roomId: currentRoomId
        }));
      }
    }
  }

  const handleErrorMessage = async (data: WebSocketMessage) => {
    if (data.type === 'token-missing' || data.type === 'auth' || data.type === 'token-invalid') {
      console.log('ERROR erkannt, starte Re-Authentifizierung...', { type: data.type, message: data.message });
      const success = await onReAuthenticate();
      if (success) {
        console.log('TOKEN_INVALID: Re-authentication successful - Entferne Fehlermeldung und verbinde neu');
        // Entferne Fehlermeldung, da Re-Auth erfolgreich war
        removeErrorMessage();
        // Reinitialize the WebSocket connection here
        const authToken = localStorage.getItem('sessionToken');
        if (authToken) {
          const wsUrl = `${getDomainCfg().chatSettings.chatUrl.replace(/^http/, 'ws')}ws?token=${authToken}`;
          setSocketUrl(wsUrl);
        }
      } else {
        console.log('TOKEN_INVALID: Re-authentication failed');
        setErrorMessage('Chatsitzung abgelaufen. Bitte hier Klicken um Chat neu zu laden - Dein Chatverlauf bleibt gespeichert (Meldung: Erneuerung der Authentifizierung fehlgeschlagen)');
      }
    } else if (data.type === 'session-invalid' || data.type === 'room-access') {
      try {
        await initializeWorkflowAndSetState();
      } catch (error) {
        console.error('Fehler beim Neustarten des Workflows:', error);
      }
    } else if (data.type === 'quota-error') {
      console.log('quota error', data)
      setErrorMessage("Creditslimit erreicht. Bitte hier Klicken um Chat neu zu laden - Dein Chatverlauf bleibt gespeichert (Meldung: Creditslimit erreicht)");
    } else {
      console.log('unknown error, try reload', data);
      setErrorMessage("Chat kann nicht abgerufen werden. Bitte hier Klicken um Chat neu zu laden - Dein Chatverlauf bleibt gespeichert (Meldung: Unbekannt)");
    }
  }

  const handleStreamingMessage = (data: WebSocketMessage) => {
    switch (data.action) {
      case 'start':
        setIsStreaming(true);
        if (!responseStartTimeRef.current) {
          responseStartTimeRef.current = Date.now();
        }
        setServerIsResponding(true);
        setStreamedContent('');
        //console.log('SupportChatWebSocket: WS Streaming start', data);
        break;
      case 'chunk':
        if (!isStreaming) {
          setIsStreaming(true);
          if (!responseStartTimeRef.current) {
            responseStartTimeRef.current = Date.now();
          }
          setServerIsResponding(true);
        }
        setStreamedContent(prev => prev + (data.message || ''));
        break;
      case 'end':
        setIsStreaming(false);
        setServerIsResponding(false);
        responseStartTimeRef.current = null;
        console.log('SupportChatWebSocket: WS Streaming end', streamedContent);
        setStreamedContent('');
        sendWebSocketMessage(JSON.stringify({
          type: 'room',
          action: 'getMessages',
          roomId: currentRoomId
        }));

        break;
    }
  };

  const updateWorkflowStatus = (status: any) => {
    setWfStatus(status);
    updateTypingMessage(status);

    sendWebSocketMessage(JSON.stringify({
      type: 'room',
      action: 'getMessages',
      roomId: currentRoomId
    }));

    switch (status.stepState) {
      case 'PROCESSING':
        if (!responseStartTimeRef.current) {
          responseStartTimeRef.current = Date.now();
        }
        setServerIsResponding(true);
        break;
      //case 'WAITING':
      case 'FINISHED':
      case 'STOPPED':
        setServerIsResponding(false);
        responseStartTimeRef.current = null;
        setMessages((prevMessages) => prevMessages.slice(0, -1));
        break;
    }
  };

  const triggerPostEvaluation = async (messages: RoomMessage[]) => {
    if (!isStreaming && !evaluatingChat && (editorState.project?.subTasks || editorState.project?.eval) && messages.length > 0) {
      setEvaluatingChat(true);
      setTimeout(async () => {
        //console.log('SupportChatWebSocket: Triggering post-evaluation', messages);
        await runChatPostEvaluation(messages);
      }, 1000);
      setEvaluatingChat(false);
    }
  };

  const updateMessages = useCallback(() => {
    const serverMessages = roomMessages
      .filter((msg) => msg != null && msg.sender !== 'System')
      .map((msg) => ({
        user: msg.sender,
        color: msg.sender === getDomainCfg().chatSettings.botName ? "green" : "#0d6efd",
        messageType: msg.role,
        msg: msg.message,
        timestamp: msg.timestamp
      }));

    const startMessage = {
      user: editorState.project?.botName || getDomainCfg().chatSettings?.botName,
      color: 'green',
      messageType: 'systemprompt',
      msg: editorState.project?.startMessage || getDomainCfg().chatSettings?.startMessage,
      timestamp: new Date(),
    };

    const newMessages = [startMessage, ...serverMessages];

    if (chatState.data?.credits && chatState.data?.credits?.credits <= 0) {
      const quotaMessage = {
        user: editorState.project?.botName || getDomainCfg().chatSettings?.botName,
        color: 'red',
        messageType: 'warning',
        msg: getDomainCfg().chatSettings?.quotaMessage,
        timestamp: new Date(),
      };

      if (!newMessages.some((message) => message.msg === getDomainCfg().chatSettings.quotaMessage)) {
        newMessages.push(quotaMessage);
      }
    }

    if (serverIsResponding) {
      const temporaryMessage = isStreaming ? streamedContent : getRotatingMessage();
      newMessages.push({
        user: editorState.project?.botName || getDomainCfg().chatSettings?.botName,
        color: 'green',
        messageType: 'typing',
        msg: temporaryMessage + '.'.repeat(typingDots),
        timestamp: new Date(),
      });
    }

    setMessages(newMessages);
    //console.log("SupportChatWebSocket: updatedMessages", newMessages);
  }, [roomMessages, serverIsResponding, typingMessage, typingDots, isAuthenticated, isStreaming, streamedContent]);

  useEffect(() => {
    updateMessages();
  }, [roomMessages, updateMessages]);

  useEffect(() => {
    const handleStatusChange = async () => {
      if (wfStatus.stepState === 'FINISHED' || wfStatus.stepState === 'STOPPED') {
        triggerPostEvaluation(roomMessages);
        await saveMessagesState(roomMessages);
      }
    };
    handleStatusChange();
  }, [roomMessages, wfStatus.stepState]);


  const saveMessagesState = async (messagesToSave?: RoomMessage[]) => {
    // Store the latest messages we want to save
    pendingMessagesRef.current = messagesToSave !== undefined ? messagesToSave : roomMessages;

    if (savePending) {
      return;
    }

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set the pending flag
    setSavePending(true);

    // Set a new timeout
    saveTimeoutRef.current = setTimeout(async () => {
      const currentEditorState = getViewData(EDITOR_VIEW_NAME);
      const updatedEditorState = {
        ...currentEditorState,
        data: {
          ...currentEditorState.data,
          chatMessages: pendingMessagesRef.current || [], // Use the latest pending messages
          rightTab: currentEditorState.data?.rightTab, // Explizit erhalten, damit Chat-Tab nicht verloren geht
        },
      };
      setEditorState(updatedEditorState);
      await saveUserData();
      setSavePending(false);
      pendingMessagesRef.current = null; // Clear the pending messages
      console.log("SupportChatWebSocket: savedMessagesState", updatedEditorState);
    }, 5000); // 5 second delay
  };

  const handleModelChange = async (modelId: string) => {
    // Finde das ausgewählte Modell in der Konfiguration
    const selectedModel = modelOptions.find(opt => opt.id === modelId);

    // Setze das Modell immer (auch wenn nicht verfügbar)
    setSelectedModelId(modelId);

    // Prüfe, ob das Modell für den Benutzer verfügbar ist
    if (!selectedModel || !isModelAvailable(selectedModel)) {
      // Öffne Upgrade-Seite in neuem Tab
      const upgradeUrl = chatState?.cfg?.upgradeUrl || "https://app.vibeventure.de/produkte/";
      window.open(upgradeUrl, '_blank');
      // Speichere das Modell trotzdem (für UI-Zustand)
      const updatedEditorState = {
        ...editorState,
        data: {
          ...editorState.data,
          lastUsedModelId: modelId,
          rightTab: editorState.data?.rightTab, // Explizit erhalten, damit Chat-Tab nicht verloren geht
        },
      };
      setEditorState(updatedEditorState);
      // Speichere die Daten
      await saveUserData();
      return;
    }

    // Speichere das Modell im editorState
    const updatedEditorState = {
      ...editorState,
      data: {
        ...editorState.data,
        lastUsedModelId: modelId,
        rightTab: editorState.data?.rightTab, // Explizit erhalten, damit Chat-Tab nicht verloren geht
      },
    };
    setEditorState(updatedEditorState);
    // Speichere die Daten
    await saveUserData();
  };

  const setErrorMessage = (errorMessage: string) => {
    setMessages((prevMessages) => {
      if (!prevMessages.some(msg => msg.messageType === 'error')) {
        return [...prevMessages, {
          user: translate('backend-error'),
          color: 'red',
          messageType: 'error',
          msg: errorMessage,
          timestamp: new Date()
        }];
      }
      return prevMessages;
    });
  };

  const removeErrorMessage = () => {
    setMessages((prevMessages) => {
      return prevMessages.filter(msg => msg.messageType !== 'error');
    });
  };

  const updateTypingMessage = (workflowStatus: any) => {
    const statusMessages = chatState.cfg.statusMessages;
    const currentStep = workflowStatus.currentStep;
    const matchingMessage = statusMessages.find((message) => message.step === currentStep);
    if (matchingMessage) {
      setTypingMessage(matchingMessage.message);
    }
  };

  const getRotatingMessage = (): string => {
    if (!responseStartTimeRef.current) {
      return typingMessage;
    }

    const elapsedSeconds = (Date.now() - responseStartTimeRef.current) / 1000;

    // Wenn eine spezifische Workflow-Status-Nachricht gesetzt ist, diese als Basis verwenden
    // Ansonsten rotierende Nachrichten verwenden
    const hasWorkflowMessage = typingMessage && typingMessage !== translate('chat-server-responding');
    let baseMessage = hasWorkflowMessage ? typingMessage : rotatingMessages[Math.floor(elapsedSeconds / 2) % rotatingMessages.length];

    // Nach 5 Sekunden: Zusatzmeldung hinzufügen
    if (elapsedSeconds >= 5 && elapsedSeconds < 10) {
      baseMessage += ' - Das kann noch ein paar Sekunden dauern';
    }
    // Nach 10 Sekunden: Erweiterte Meldung
    else if (elapsedSeconds >= 10) {
      baseMessage += ' - Das kann noch ein paar Sekunden dauern...';
    }

    return baseMessage;
  };

  const initializeWorkflowAndSetState = async () => {
    const userContext = editorState.data?.projectContent?.contextContent;
    const wfDefinition = {
      ...editorState.project?.definition,
      context: { userContext }
    }
    //TODO: check if room or sessionId should be transfered, too if available...
    const workflowData = await api.initializeWorkflow(
      editorState.project?.workflow || getDomainCfg().chatSettings.workflow,
      wfDefinition,
      editorState.data?.chatMessages,
      llmSwitchAvailable ? selectedModelId : undefined
    );
    setWorkflowSessionId(workflowData.sessionId);
    setCurrentRoomId(workflowData.roomId);
    console.log("SupportChatWebSocket: initializeWorkflowAndSetState", workflowData.sessionId, workflowData.roomId, workflowData);
    return workflowData.roomId;
  };

  const sendChatMessage = async (msg: string) => {
    const userMessage = {
      user: getDomainCfg().chatSettings.userName,
      color: 'blue',
      messageType: 'user',
      msg: msg,
      timestamp: new Date(),
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    if (!responseStartTimeRef.current) {
      responseStartTimeRef.current = Date.now();
    }
    setServerIsResponding(true);

    try {
      let roomId = currentRoomId;
      if (!hasSentFirstMessage) {
        roomId = await initializeWorkflowAndSetState();
        setHasSentFirstMessage(true);
      }

      if (roomId) {
        sendWebSocketMessage(JSON.stringify({
          type: 'room',
          roomId,
          message: msg,
        }));
      }
      //console.log("SupportChatWebSocket: sendChatMessage", msg);
    } catch (error) {
      console.error('Error sending message:', error);
      setErrorMessage('Nachricht konnte nicht gesendet werden. Bitte hier Klicken um Chat neu zu laden - Dein Chatverlauf bleibt gespeichert');
      setServerIsResponding(false);
      responseStartTimeRef.current = null;
    }
  };

  useEffect(() => {
    if (serverIsResponding) {
      if (workflowSessionId) {
        sendWebSocketMessage(JSON.stringify({
          type: 'workflow',
          action: 'getStatus',
          sessionId: workflowSessionId,
        }));
        //console.log("SupportChatWebSocket: getWorkflowStatus", workflowSessionId);
      }
      typingTimer.current = setTimeout(() => {
        setTypingDots((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setTypingDots(0);
    }

    return () => {
      if (typingTimer.current) clearTimeout(typingTimer.current);
    };
  }, [typingDots, serverIsResponding, workflowSessionId, sendWebSocketMessage]);

  useEffect(() => {
    if (resetMessages) {
      setMessages([]);
      setWorkflowSessionId(null);
      setCurrentRoomId(null);
      setRoomMessages([]);
      setHasSentFirstMessage(false);
      responseStartTimeRef.current = null;
      saveMessagesState([]);
    }
  }, [resetMessages]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      pendingMessagesRef.current = null;
    };
  }, []);

  return (
    <BaseChat
      messages={messages}
      sendMessage={sendChatMessage}
      serverIsResponding={serverIsResponding}
      hasProjectContext={hasProjectContext}
      resetChat={resetMessages}
      llmSwitchAvailable={llmSwitchAvailable}
      modelOptions={modelOptions}
      selectedModelId={selectedModelId}
      onChangeModel={handleModelChange}
      isModelAvailable={isModelAvailable}
    />
  );
};

export default SupportChatWebSocket;